import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { EmptyState } from '@/components/ui/States';
import { searchPatients } from '@/features/patients/api';
import type { Patient } from '@/features/patients/types';
import PatientPrescriptionHistory from '@/features/prescriptions/PatientPrescriptionHistory';

/**
 * Pharmacy's entry point into the cross-visit prescription history/close-out
 * screen — Patient Records already has the patient in context when it
 * renders the same PatientPrescriptionHistory component, but Pharmacy needs
 * its own patient search first. Deliberately the plain roster search
 * (GET /patients/search, shared with Billing/Appointments/Records — see
 * routes/patients.js's requirePatientList, widened to include 'pharmacy' for
 * exactly this) rather than the walk-in counter's customer search, which is
 * scoped to pharmacy:outpatient and returns counter-specific fields
 * (registration_source, points_balance) this screen has no use for.
 */
export default function RxHistoryTab() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Patient[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<Patient | null>(null);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    setSearching(true);
    const handle = setTimeout(() => {
      searchPatients(query.trim())
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  return (
    <div className="grid gap-4 min-[900px]:grid-cols-2">
      <Card>
        <div className="mb-3.5 text-sm font-semibold">Find patient</div>
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
          placeholder="Search by name, mobile or UHID…"
        />
        {searching && <div className="mt-2.5 text-xs text-ink-muted">Searching…</div>}
        {!searching && query.trim() && results.length === 0 && (
          <div className="mt-2.5 text-xs text-ink-muted">No matching patient found.</div>
        )}
        {results.map((p) => (
          <div
            key={p.id}
            onClick={() => setSelected(p)}
            className="mt-2.5 cursor-pointer rounded-[var(--radius)] border p-[11px]"
            style={{
              borderColor: selected?.id === p.id ? 'var(--teal)' : '#E5E7EB',
              background: selected?.id === p.id ? 'rgba(11,95,214,0.06)' : 'transparent',
            }}
          >
            <div className="text-[13px] font-semibold">{p.name}</div>
            <div className="mt-0.5 text-[11px] text-ink-muted">{p.uhid} · {p.mobile}</div>
          </div>
        ))}
      </Card>

      {selected ? (
        <PatientPrescriptionHistory uhid={selected.uhid} />
      ) : (
        <Card>
          <EmptyState label="Search for a patient to see their prescriptions across every visit" />
        </Card>
      )}
    </div>
  );
}
